import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Stack,
  IconButton,
  Chip,
  Divider,
  Skeleton,
  alpha,
  useTheme,
  Avatar,
  Dialog,
} from "@mui/material";
import {
  ArrowBack,
  ShoppingBag,
  VerifiedUser,
  LocalShipping,
  Replay,
  Close,
} from "@mui/icons-material";
import { getOneProduct, orderRequest } from "../redux/slices/product/thunk";

const BASE_URL = "https://red-90.com";
const joinUrl = (path) =>
  path?.startsWith("http") ? path : `${BASE_URL}${path}`;

export default function ProductDetails() {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const primaryMain = theme.palette.primary.main;

  const { oneProduct, isLoading } = useSelector((state) => state.product);

  // States للطلب
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (id) {
      dispatch(getOneProduct(id));
    }
  }, [id, dispatch]);

  const confirmOrder = () => {
    // console.log("Ordered:", { products: oneProduct._id, quantity });
    const data = { product: oneProduct._id, quantity }
    dispatch(orderRequest({ data }))
    setOrderDialogOpen(false);
  };

  if (isLoading) return <ProductLoader />;

  if (!oneProduct) return <NoProductFound />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 8 }}>
      {/* Navbar البديل */}
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ mb: 2, bgcolor: "background.paper" }}
        >
          <ArrowBack sx={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
        </IconButton>

        <Grid container spacing={6}>
          {/* Image Section */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                width: "100%",
                borderRadius: 8,
                overflow: "hidden",
                bgcolor: "background.paper",
                p: 4,
                textAlign: "center",
                boxShadow: `0 20px 50px ${alpha("#000", 0.05)}`,
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={joinUrl(oneProduct.image)}
                sx={{ width: "100%", maxHeight: 500, objectFit: "contain" }}
              />
              <Chip
                label={isRtl ? oneProduct.category_ar : oneProduct.category_en}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  fontWeight: 900,
                }}
              />
            </Box>
          </Grid>

          {/* Details Section */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h3" fontWeight={1000} gutterBottom>
                  {isRtl ? oneProduct.title_ar : oneProduct.title_en}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="h4" color="primary" fontWeight={1000}>
                    {new Intl.NumberFormat(i18n.language).format(
                      oneProduct.price,
                    )}{" "}
                    EGP
                  </Typography>
                  {oneProduct.stock <= 5 && (
                    <Chip
                      label={
                        isRtl
                          ? `متبقي ${oneProduct.stock} فقط!`
                          : `Only ${oneProduct.stock} left!`
                      }
                      color="error"
                      variant="outlined"
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  {isRtl ? "وصف المنتج" : "Description"}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  {isRtl
                    ? oneProduct.description_ar
                    : oneProduct.description_en}
                  {/* في حال عدم وجود وصف في الـ schema، يمكنك وضع نص تجريبي أو إخفاء القسم */}
                </Typography>
              </Box>

              {/* Trust Badges */}
              {/* Trust Badges - Quality & Excellence */}
              <Grid container spacing={2}>
                <TrustItem
                  icon={<VerifiedUser color="primary" />}
                  text={isRtl ? "جودة متميزة" : "Premium Quality"}
                />
                <TrustItem
                  icon={
                    <Box component="span" sx={{ fontSize: 24 }}>
                      ✨
                    </Box>
                  }
                  text={isRtl ? "أصالة المنتج" : "100% Authentic"}
                />
                <TrustItem
                  icon={
                    <Box component="span" sx={{ fontSize: 24 }}>
                      🏆
                    </Box>
                  }
                  text={isRtl ? "اختيار النخبة" : "Top Rated"}
                />
              </Grid>

              <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingBag />}
                  onClick={() => setOrderDialogOpen(true)}
                  sx={{
                    borderRadius: 4,
                    py: 2,
                    fontWeight: 1000,
                    fontSize: "1.1rem",
                  }}
                >
                  {t("home.products.order_now")}
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Re-using the Order Dialog logic */}
      <OrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        product={oneProduct}
        quantity={quantity}
        setQuantity={setQuantity}
        confirmOrder={confirmOrder}
        isRtl={isRtl}
        primaryMain={primaryMain}
        t={t}
        i18n={i18n}
      />
    </Box>
  );
}

// مكونات فرعية مساعدة (Sub-components)
function TrustItem({ icon, text }) {
  return (
    <Grid item xs={4}>
      <Stack alignItems="center" spacing={1} sx={{ textAlign: "center" }}>
        {icon}
        <Typography variant="caption" fontWeight={800}>
          {text}
        </Typography>
      </Stack>
    </Grid>
  );
}

function ProductLoader() {
  return (
    <Container maxWidth="lg" sx={{ py: 10 }}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <Skeleton
            variant="rectangular"
            height={500}
            sx={{ borderRadius: 8 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Skeleton width="60%" height={60} />
          <Skeleton width="40%" height={40} sx={{ my: 2 }} />
          <Skeleton variant="text" sx={{ mt: 5 }} />
          <Skeleton variant="text" />
          <Skeleton variant="text" width="80%" />
        </Grid>
      </Grid>
    </Container>
  );
}

function NoProductFound() {
  return (
    <Box sx={{ textAlign: "center", py: 20 }}>
      <Typography variant="h5">Product not found!</Typography>
    </Box>
  );
}

// الـ Dialog كـ Component مستقل لتقليل تكرار الكود
function OrderDialog({
  open,
  onClose,
  product,
  quantity,
  setQuantity,
  confirmOrder,
  isRtl,
  primaryMain,
  t,
  i18n,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: 7, p: 1, maxWidth: 420, width: "100%" },
      }}
    >
      <Box sx={{ textAlign: "center", p: 3 }}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 15, top: 15 }}
        >
          <Close />
        </IconButton>
        <Avatar
          src={joinUrl(product?.image)}
          sx={{
            width: 120,
            height: 120,
            mx: "auto",
            mb: 2,
            border: `4px solid ${alpha(primaryMain, 0.1)}`,
          }}
        />
        <Typography variant="h5" fontWeight={1000}>
          {isRtl ? product?.title_ar : product?.title_en}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            my: 4,
          }}
        >
          <Button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            sx={{ fontSize: 25, fontWeight: 900 }}
          >
            -
          </Button>
          <Typography variant="h4" sx={{ mx: 4, fontWeight: 1000 }}>
            {new Intl.NumberFormat(i18n.language).format(quantity)}
          </Typography>
          <Button
            onClick={() => setQuantity(quantity + 1)}
            sx={{ fontSize: 25, fontWeight: 900 }}
          >
            +
          </Button>
        </Box>
        <Button
          fullWidth
          variant="contained"
          onClick={confirmOrder}
          sx={{ borderRadius: 4, py: 2, fontWeight: 900 }}
        >
          {t("home.products.confirm_order")} (
          {new Intl.NumberFormat(i18n.language).format(
            product?.price * quantity,
          )}{" "}
          EGP
        </Button>
      </Box>
    </Dialog>
  );
}
